import type { CapacityRead, RevenueUplift, ThroughputScenario, WaterfallStep } from '@/versions/v1/schema/capacity';
import { formatCurrency, recoveryCapPhrase } from './Scene.transform';
import { bottleneckLadderProjection } from './BottleneckLadder.transform';
import { CURRENCY_GLYPH } from '@/versions/v1/shared/money';

export type ConstraintBand = 'green' | 'amber' | 'orange' | 'red';

export type ConstraintBar = {
  id: string;
  label: string;
  utilisationPct: number;
  fillPct: number;
  band: ConstraintBand;
  tone: 'ok' | 'warn' | 'bad';
  fillColor: string;
  note: string;
  ceilingStph: number;
  headroomStph: number;
  gradeBasis: string;
  bottleneck: boolean;
};

export type MetricCard = {
  id: string;
  label: string;
  value: string;
  unit: string;
  sub: string;
  tone: 'ok' | 'warn' | 'bad' | 'neutral';
};

export type ScenarioCard = {
  id: string;
  label: string;
  throughput: string;
  throughputComparison: string;
  recovery: string;
  recoveryComparison: string;
  ebitda: string;
  ebitdaComparison: string;
  selectable: boolean;
  /** Compact row: "Current · 71.7 stph". */
  name: string;
  /** "Zn 82.3% · Pb 85.1%", or the Zn recovery alone beside the No capital pill. */
  sub: string;
  /** The row's EBITDA move: vs Maximum on Current, vs Current elsewhere. */
  valueSub: string;
  valueSubTone: 'ok' | 'bad' | 'neutral';
  noCapital: boolean;
};

/** One cell of the ladder card's hairline strip. */
export type LadderStripCell = {
  id: string;
  label: string;
  value: string;
  sub: string;
  tone: 'ok' | 'warn' | 'bad' | 'neutral';
};

export type SelectableScenarioId = Extract<ThroughputScenario['id'], 'current' | 'optimised' | 'maximum'>;

export type UpliftCard = {
  heading?: string;
  tone?: 'success' | 'danger' | 'default';
  fromTo: string;
  additional: string;
  rows: readonly { label: string; value: string; tone?: 'ok' | 'warn' | 'bad' | 'neutral' }[];
};

export type WaterfallProjection = {
  rows: readonly WaterfallStep[];
  bars: readonly {
    id: string;
    label: string;
    labelLines: readonly string[];
    valueLabel: string;
    x: number;
    width: number;
    y: number;
    height: number;
    tone: 'info' | 'ok' | 'bad' | 'neutral';
    /** A running total (Current, Maximum, With capacity, No capital) rather than a movement. */
    total: boolean;
    /** Axis name and compact value as the comp draws them ("Cur", "53.3k"). */
    shortLabel: string;
    shortValue: string;
    selected: boolean;
    /**
     * False for a bar that repeats another drawn bar: at a feed-limited grade
     * Maximum equals Without capital, so Step 2 potential is the same move as
     * Capacity value and Without capital the same total as Maximum. The bar
     * keeps its engine row, tone and geometry (its twin's) but is not drawn.
     */
    drawn: boolean;
  }[];
  /** Engine row ids in drawn order; the running total reads left to right. */
  drawnOrder: readonly string[];
  connectors: readonly { x1: number; x2: number; y: number }[];
  baselineY: number;
  viewWidth: number;
  viewHeight: number;
  /** SVG text sizes in px at the rendered width (the chart draws at 1:1). */
  labelSize: number;
  valueSize: number;
  lowA: number;
  highA: number;
  selectedBarId: string;
  selectedLanding?: {
    barId: string;
    scenarioLabel: string;
    valueA: number;
    valueLabel: string;
    x: number;
    y: number;
  };
};

export type ThroughputProjection = {
  selectedScenarioId: SelectableScenarioId;
  selectedHeadroomStph: number;
  selectedEbitdaA: number;
  metricCards: readonly MetricCard[];
  /** The ladder card's four-cell strip; at a feed-limited grade it names the next ceiling instead of a maximum. */
  ladderStrip: readonly LadderStripCell[];
  ladderMeta: string;
  feedLimited: boolean;
  constraints: readonly ConstraintBar[];
  chart: {
    categories: readonly string[];
    zn: readonly number[];
    pb: readonly number[];
    currentIndex: number;
    maximumIndex: number;
    selectedIndex: number;
    currentEqualsMaximum: boolean;
    selectedLabel: string;
    floorPct: number;
    yDomain: readonly [number, number];
  };
  scenarios: readonly ScenarioCard[];
  step1: UpliftCard;
  step2: UpliftCard;
  waterfall: WaterfallProjection;
  ai: readonly { label: string; value: string; tone: 'ok' | 'warn' | 'bad' | 'neutral' }[];
};

function bandFor(utilisationPct: number): ConstraintBand {
  if (utilisationPct > 95) return 'red';
  if (utilisationPct >= 85) return 'orange';
  if (utilisationPct >= 70) return 'amber';
  return 'green';
}

function toneForBand(band: ConstraintBand): ConstraintBar['tone'] {
  if (band === 'green') return 'ok';
  if (band === 'red') return 'bad';
  return 'warn';
}

function fillColorForBand(band: ConstraintBand): string {
  if (band === 'green') return 'var(--state-online)';
  if (band === 'amber') return 'var(--state-warning)';
  if (band === 'orange') return 'color-mix(in srgb, var(--state-warning) 60%, var(--state-blocked))';
  return 'var(--state-blocked)';
}

function signedStph(value: number): string {
  return signedStphOneDecimal(value);
}

function signedStphOneDecimal(value: number): string {
  const sign = value >= 0 ? '+' : '−';
  const magnitude = (Math.round((Math.abs(value) + 1e-9) * 10) / 10).toFixed(1);
  return `${sign}${magnitude} stph`;
}

function stphOneDecimal(value: number): string {
  return (Math.round((value + 1e-9) * 10) / 10).toFixed(1);
}

function signedPpt(value: number): string {
  return `${value >= 0 ? '+' : '−'}${Math.abs(value).toFixed(1)} pt`;
}

function signedA(value: number): string {
  return `${value === 0 ? '' : value > 0 ? '+' : '−'}${formatCurrency(Math.abs(value))}/day`;
}

function signedAnnualA(value: number): string {
  return `${value === 0 ? '' : value > 0 ? '+' : '−'}${formatCurrency(Math.abs(value))}/yr`;
}

function signTone(value: number): 'ok' | 'bad' | 'neutral' {
  return value > 0 ? 'ok' : value < 0 ? 'bad' : 'neutral';
}

function upliftCard(uplift: RevenueUplift, znRecoveryPct: number, pbRecoveryPct: number): UpliftCard {
  return {
    fromTo: `${stphOneDecimal(uplift.fromStph)} stph → ${stphOneDecimal(uplift.toStph)} stph`,
    heading: 'Operating · no capital',
    tone: uplift.ebitdaGainA > 0 ? 'success' : uplift.ebitdaGainA < 0 ? 'danger' : 'default',
    additional: uplift.additionalStph === 0
      ? `No operating headroom · ${CURRENCY_GLYPH}0/day`
      : uplift.ebitdaGainA < 0
        ? `${signedStph(uplift.additionalStph)} · Running headroom loses ${formatCurrency(Math.abs(uplift.ebitdaGainA))}/day at this grade`
        : `${signedStph(uplift.additionalStph)} · ${uplift.additionalDstPerDay.toFixed(0)} dst/day`,
    rows: [
      {
        label: 'Zn concentrate gain',
        value: `${uplift.zincConcentrateGainDmtPerDay.toFixed(1)} dmt/day`,
      },
      {
        label: 'Pb/Ag concentrate gain',
        value: `${uplift.leadConcentrateGainDmtPerDay.toFixed(1)} dmt/day`,
      },
      { label: 'NSR change', value: signedA(uplift.nsrGainA), tone: signTone(uplift.nsrGainA) },
      { label: uplift.ebitdaGainA < 0 ? 'EBITDA loss' : uplift.ebitdaGainA === 0 ? 'EBITDA change' : 'EBITDA uplift', value: signedA(uplift.ebitdaGainA), tone: signTone(uplift.ebitdaGainA) },
      { label: 'Annualised', value: signedAnnualA(uplift.annualisedEbitdaGainA), tone: signTone(uplift.annualisedEbitdaGainA) },
      { label: 'Recovery', value: `Zn ${znRecoveryPct.toFixed(1)}% · Pb ${pbRecoveryPct.toFixed(1)}%` },
    ],
  };
}

function scenarioCards(read: CapacityRead): ScenarioCard[] {
  const current = read.scenarios.find((scenario) => scenario.id === 'current')!;
  const maximum = read.scenarios.find((scenario) => scenario.id === 'maximum')!;
  return read.scenarios.map((scenario) => ({
    id: scenario.id,
    label: scenario.label,
    throughput: `${stphOneDecimal(scenario.throughputStph)} stph`,
    throughputComparison: scenario.id === 'current'
      ? `${signedStph(scenario.throughputStph - read.targetThroughputStph)} vs ${stphOneDecimal(read.targetThroughputStph)} stph optimised`
      : `${signedStph(scenario.throughputStph - current.throughputStph)} vs current ${stphOneDecimal(current.throughputStph)} stph`,
    recovery: `Zn ${scenario.recoveries.znPct.toFixed(1)}% · Pb ${scenario.recoveries.pbPct.toFixed(1)}%`,
    recoveryComparison: scenario.recoveryCapped
      ? scenario.cappedRecoveryMetals.map(recoveryCapPhrase).join(' · ')
      : scenario.id === 'current'
      ? `Zn ${signedPpt(scenario.recoveries.znPct - read.recoveryFloorZnPct)} vs ${read.recoveryFloorZnPct.toFixed(1)}% floor · Pb ${signedPpt(scenario.recoveries.pbPct - read.recoveryFloorPbPct)} vs ${read.recoveryFloorPbPct.toFixed(1)}% floor`
      : `Zn ${signedPpt(scenario.recoveries.znPct - current.recoveries.znPct)} · Pb ${signedPpt(scenario.recoveries.pbPct - current.recoveries.pbPct)} vs current`,
    ebitda: formatCurrency(scenario.financials.ebitdaA),
    ebitdaComparison: scenario.id === 'current'
      ? `${signedA(scenario.financials.ebitdaA - maximum.financials.ebitdaA)} vs Maximum ${formatCurrency(maximum.financials.ebitdaA)}`
      : `${signedA(scenario.financials.ebitdaA - current.financials.ebitdaA)} vs today's ${formatCurrency(current.financials.ebitdaA)}`,
    selectable: scenario.selectable,
    name: scenario.label.length <= 10
      ? `${scenario.label} · ${stphOneDecimal(scenario.throughputStph)} stph`
      : `${scenario.label} · ${stphOneDecimal(scenario.throughputStph)}`,
    sub: scenario.selectable
      ? `Zn ${scenario.recoveries.znPct.toFixed(1)}% · Pb ${scenario.recoveries.pbPct.toFixed(1)}%`
      : `Zn ${scenario.recoveries.znPct.toFixed(1)}%`,
    valueSub: scenario.id === 'current'
      ? `${signedShortA(scenario.financials.ebitdaA - maximum.financials.ebitdaA)} vs Max`
      : signedShortA(scenario.financials.ebitdaA - current.financials.ebitdaA),
    valueSubTone: signTone(scenario.id === 'current'
      ? scenario.financials.ebitdaA - maximum.financials.ebitdaA
      : scenario.financials.ebitdaA - current.financials.ebitdaA),
    noCapital: !scenario.selectable,
  }));
}

/** "+US$1,080" — a scenario row's move, without the /day the card's unit already carries. */
function signedShortA(value: number): string {
  const rounded = Math.round(value);
  return `${rounded === 0 ? '' : rounded > 0 ? '+' : '−'}${formatCurrency(Math.abs(value))}`;
}

/** A running-total waterfall read on a cropped value axis (the comp's), so steps of a few thousand stay visible. */
function waterfallDomain(rows: readonly WaterfallStep[]): { lowA: number; highA: number } {
  const values = rows.flatMap((row) => row.kind === 'total' ? [row.endA] : [row.startA, row.endA]);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(max - min, 1_000);
  const lowA = Math.max(0, Math.floor((min - span * 0.6) / 5_000) * 5_000);
  return { lowA, highA: Math.ceil((max + span * 0.2) / 5_000) * 5_000 };
}

const WATERFALL_SHORT_LABEL: Record<string, string> = {
  current: 'Cur',
  step1: 'S1',
  maximum: 'Max',
  'step2-potential': 'S2',
  'step2-maintained': 'Cap',
  'step2-no-capital': 'No cap',
};

function compactK(value: number, signed: boolean): string {
  if (Math.abs(value) < 50) return '0';
  const magnitude = `${(Math.abs(value) / 1_000).toFixed(1)}k`;
  if (!signed) return magnitude;
  return `${value > 0 ? '+' : value < 0 ? '−' : ''}${magnitude}`;
}

/**
 * The drawn order. Normally the engine's: Current, Step 1, Maximum, Step 2,
 * With capacity, Recovery floor cost, Without capital. At a feed-limited grade
 * the engine's Capacity value runs from Without capital up to With capacity,
 * so it is drawn after Maximum (equal to Without capital there) and before the
 * With capacity total, and every bar reads upward: Current → Step 1 → Maximum
 * → Capacity value → With capacity. Step 2 potential and Without capital repeat
 * those bars and are not drawn.
 */
function waterfallDrawnOrder(rows: readonly WaterfallStep[], feedLimited: boolean): readonly string[] {
  const ids = rows.map((row) => row.id);
  if (!feedLimited) return ids;
  const value = rows.find((row) => row.id === 'recovery-floor-cost');
  if (!value) return ids;
  return ['current', 'step1', 'maximum', 'recovery-floor-cost', 'step2-maintained'].filter((id) => ids.includes(id));
}

function waterfallChart(
  rows: readonly WaterfallStep[],
  selectedBarId: string,
  selectedLanding?: { scenarioLabel: string; valueA: number },
  width = 360,
  feedLimited = false,
): WaterfallProjection {
  const viewWidth = Math.max(240, width);
  const viewHeight = 200;
  const plotTop = 20;
  const baselineY = viewHeight - 28;
  const left = 4;
  const right = 4;
  const drawnOrder = waterfallDrawnOrder(rows, feedLimited);
  const byId = new Map(rows.map((row) => [row.id, row]));
  const drawnRows = drawnOrder.map((id) => byId.get(id)!);
  const slot = (viewWidth - left - right) / drawnRows.length;
  const barWidth = slot * 0.68;
  const { lowA, highA } = waterfallDomain(drawnRows);
  const yFor = (value: number) => baselineY - ((value - lowA) / (highA - lowA)) * (baselineY - plotTop);
  // An undrawn bar takes its twin's slot: Step 2 potential that of Capacity value, Without capital that of Maximum.
  const slotOf = (id: string) => {
    if (drawnOrder.includes(id)) return drawnOrder.indexOf(id);
    const twin = id === 'step2-potential' ? 'recovery-floor-cost' : id === 'step2-no-capital' ? 'maximum' : id;
    return Math.max(0, drawnOrder.indexOf(twin));
  };
  const allBars = rows.map((row) => {
    const x = left + slotOf(row.id) * slot + slot * 0.16;
    const total = row.kind === 'total';
    const startY = yFor(total ? lowA : row.startA);
    const endY = yFor(row.endA);
    return {
      id: row.id,
      label: row.label,
      labelLines: [row.label],
      valueLabel: formatCurrency(row.deltaA),
      x,
      width: barWidth,
      y: Math.min(startY, endY),
      height: Math.max(2, Math.abs(startY - endY)),
      tone: total
        ? 'neutral' as const
        : row.kind === 'gain'
          ? 'ok' as const
          : row.kind === 'loss'
          ? 'bad' as const
          : 'neutral' as const,
      total,
      shortLabel: WATERFALL_SHORT_LABEL[row.id] ?? (row.label === 'Capacity value' ? 'Value' : 'Floor'),
      shortValue: total ? compactK(row.endA, false) : compactK(row.deltaA, true),
      selected: row.id === selectedBarId,
      drawn: drawnOrder.includes(row.id),
    };
  });
  const bars = [...allBars].sort((a, b) => {
    const ai = drawnOrder.indexOf(a.id);
    const bi = drawnOrder.indexOf(b.id);
    return (ai < 0 ? Number.MAX_SAFE_INTEGER : ai) - (bi < 0 ? Number.MAX_SAFE_INTEGER : bi);
  });
  const drawnBars = bars.filter((bar) => bar.drawn);
  const connectors = drawnRows.slice(0, -1).map((row, index) => ({
    x1: drawnBars[index]!.x + drawnBars[index]!.width,
    x2: drawnBars[index + 1]!.x,
    y: yFor(row.endA),
  }));
  const landingBar = selectedLanding ? bars.find((bar) => bar.id === 'step1') : undefined;
  return {
    rows,
    bars,
    drawnOrder,
    connectors,
    baselineY,
    viewWidth,
    viewHeight,
    labelSize: 10.5,
    valueSize: 10.5,
    lowA,
    highA,
    selectedBarId,
    ...(selectedLanding && landingBar ? {
      selectedLanding: {
        barId: landingBar.id,
        scenarioLabel: selectedLanding.scenarioLabel,
        valueA: selectedLanding.valueA,
        valueLabel: formatCurrency(selectedLanding.valueA),
        x: landingBar.x + landingBar.width / 2,
        y: yFor(selectedLanding.valueA),
      },
    } : {}),
  };
}

export function throughputProjection(
  read: CapacityRead,
  requestedScenarioId: SelectableScenarioId = 'current',
  waterfallWidth = 360,
): ThroughputProjection {
  const bottleneck = read.circuits.find((row) => row.id === read.bottleneck)!;
  const feedLimited = read.currentThroughputStph < read.measuredThroughputStph;
  const maximum = read.scenarios.find((row) => row.id === 'maximum')!;
  const beyond = read.scenarios.find((row) => row.id === 'beyond-limit')!;
  const selectedScenario = read.scenarios.find((row) => row.id === requestedScenarioId && row.selectable) ??
    read.scenarios.find((row) => row.id === 'current')!;
  const selectedScenarioId = selectedScenario.id as SelectableScenarioId;
  const selectedHeadroomStph = read.maxAchievableStph - selectedScenario.throughputStph;
  const selectedEbitdaA = selectedScenario.financials.ebitdaA;
  const currentEbitdaA = read.scenarios.find((row) => row.id === 'current')!.financials.ebitdaA;
  const chartPoints = read.recoverySeries;
  const currentIndex = chartPoints.findIndex((point) => point.throughputStph === read.currentThroughputStph);
  const maximumIndex = chartPoints.findIndex((point) => point.throughputStph === maximum.throughputStph);
  const selectedIndex = chartPoints.findIndex((point) => point.throughputStph === selectedScenario.throughputStph);
  const selectedBarId = selectedScenarioId === 'current'
    ? 'current'
    : selectedScenarioId === 'optimised'
      ? ''
      : 'maximum';
  const nextRungThresholdStph = Math.max(
    selectedScenario.throughputStph,
    bottleneck.ceilingStph,
  );
  const nextRung = read.bottleneckLadder.rungs.find((rung) => rung.ceilingStph > nextRungThresholdStph) ??
    read.bottleneckLadder.rungs[read.bottleneckLadder.rungs.length - 1]!;
  const chartValues = [
    ...chartPoints.map((point) => point.znPct),
    ...chartPoints.map((point) => point.pbPct),
    read.recoveryFloorZnPct,
    read.scenarios.find((row) => row.id === 'current')!.recoveries.znPct,
    maximum.recoveries.znPct,
  ];
  const chartLow = Math.min(...chartValues);
  const chartHigh = Math.max(...chartValues);
  const chartPadding = Math.max(1, (chartHigh - chartLow) * 0.08);

  const metricCards: MetricCard[] = [
    {
      id: 'current',
      label: feedLimited ? 'Feed limited at this grade' : 'Current throughput',
      value: read.currentThroughputStph.toFixed(1),
      unit: 'stph',
      sub: feedLimited
        ? `${bottleneck.name} ceiling · ${stphOneDecimal(read.measuredThroughputStph)} stph measured`
        : `${((read.currentThroughputStph / read.targetThroughputStph) * 100).toFixed(0)}% of ${stphOneDecimal(read.targetThroughputStph)} stph optimised`,
      tone: 'neutral',
    },
    {
      id: 'maximum',
      label: 'Max achievable at current grade',
      value: read.maxAchievableStph.toFixed(1),
      unit: 'stph',
      sub: read.maxAchievableStph === read.recoveryFloorStph
        ? `Zn recovery floor ${read.recoveryFloorZnPct.toFixed(1)}%`
        : `${bottleneck.name} ceiling ${stphOneDecimal(bottleneck.ceilingStph)} stph`,
      tone: 'ok',
    },
    {
      id: 'headroom',
      label: 'Headroom available',
      value: feedLimited ? '0.0 stph' : signedStph(read.headroomStph),
      unit: '',
      sub: feedLimited ? `${bottleneck.name} ceiling reached` : `Before ${bottleneck.name} ceiling`,
      tone: read.headroomStph >= 0 ? 'warn' : 'bad',
    },
    {
      id: 'bottleneck',
      label: 'Bottleneck at current grade',
      value: bottleneck.name,
      unit: '',
      sub: `${bottleneck.utilisationPct.toFixed(0)}% utilisation`,
      tone: 'bad',
    },
  ];

  const constraints: ConstraintBar[] = read.circuits.map((circuit) => ({
    id: circuit.id,
    label: circuit.name,
    utilisationPct: circuit.utilisationPct,
    fillPct: Math.min(100, Math.max(0, circuit.utilisationPct)),
    band: bandFor(circuit.utilisationPct),
    tone: toneForBand(bandFor(circuit.utilisationPct)),
    fillColor: fillColorForBand(bandFor(circuit.utilisationPct)),
    note: circuit.id === read.bottleneck
      ? circuit.utilisationPct > 100
        ? `Over capacity at ${read.currentThroughputStph.toFixed(0)} stph · ${circuit.ceilingStph.toFixed(0)} stph ceiling`
        : `${circuit.id === 'grinding' ? `${read.sourceTags.grindingCyclone} · ` : ''}${circuit.headroomStph.toFixed(1)} stph headroom before ceiling`
      : circuit.utilisationPct > 100
        ? `Over capacity at current grade · ${circuit.ceilingStph.toFixed(0)} stph ceiling`
        : `${circuit.headroomStph.toFixed(0)} stph headroom · ${circuit.gradeBasis}`,
    ceilingStph: circuit.ceilingStph,
    headroomStph: circuit.headroomStph,
    gradeBasis: circuit.gradeBasis,
    bottleneck: circuit.id === read.bottleneck,
  }));

  const step2 = read.step2.potential;
  const step2NoCapital = read.step2.withoutCapital;
  const step2Card: UpliftCard = {
    heading: feedLimited ? `Lift ${bottleneck.name} · capital required` : undefined,
    fromTo: `${stphOneDecimal(step2.fromStph)} stph → ${stphOneDecimal(step2.toStph)} stph`,
    additional: `${signedStph(step2.additionalStph)} · ${step2.additionalDstPerDay.toFixed(0)} dst/day`,
    rows: [
      { label: 'Potential NSR change · recovery maintained', value: signedA(step2.nsrGainA), tone: signTone(step2.nsrGainA) },
      { label: 'Potential EBITDA uplift', value: signedA(step2.ebitdaGainA), tone: signTone(step2.ebitdaGainA) },
      { label: 'Annualised potential', value: signedAnnualA(step2.annualisedEbitdaGainA), tone: signTone(step2.annualisedEbitdaGainA) },
      {
        label: 'Zn recovery without capital',
        value: `${beyond.recoveries.znPct.toFixed(1)}% · ${read.recoveryFloorZnPct.toFixed(1)}% floor`,
        tone: beyond.recoveries.znPct < read.recoveryFloorZnPct ? 'bad' : 'ok',
      },
      {
        label: feedLimited ? 'Capacity value' : 'Recovery floor cost',
        value: `${formatCurrency(read.step2.recoveryFloorCostA)}/day`,
        tone: feedLimited ? signTone(read.step2.recoveryFloorCostA)
          : read.step2.recoveryFloorCostA > 0 ? 'bad' : read.step2.recoveryFloorCostA < 0 ? 'ok' : 'neutral',
      },
      { label: 'Net EBITDA vs maximum without capital', value: signedA(step2NoCapital.ebitdaGainA), tone: signTone(step2NoCapital.ebitdaGainA) },
    ],
  };

  const waterfall = waterfallChart(
    read.waterfall,
    selectedBarId,
    selectedScenarioId === 'optimised'
      ? { scenarioLabel: selectedScenario.label, valueA: selectedScenario.financials.ebitdaA }
      : undefined,
    waterfallWidth,
    feedLimited,
  );
  const nextCeiling = read.bottleneckLadder.rungs.find((rung) => rung.ceilingStph > read.currentThroughputStph + 1e-9);
  const ladderStrip: LadderStripCell[] = feedLimited
    ? [
        {
          id: 'current',
          label: 'Current throughput',
          value: `Feed limited to ${stphOneDecimal(read.currentThroughputStph)} stph`,
          sub: `by ${bottleneck.name} at this grade`,
          tone: 'bad',
        },
        {
          id: 'headroom',
          label: metricCards[2]!.label,
          value: '0 stph',
          sub: `at the ${bottleneck.name} ceiling`,
          tone: 'neutral',
        },
        {
          id: 'bottleneck',
          label: metricCards[3]!.label,
          value: metricCards[3]!.value,
          sub: 'capital to remove',
          tone: 'bad',
        },
        ...(nextCeiling ? [{
          id: 'next',
          label: 'Next ceiling',
          value: nextCeiling.name,
          sub: `${stphOneDecimal(nextCeiling.ceilingStph)} stph · ${signedStph(nextCeiling.ceilingStph - read.currentThroughputStph)}`,
          tone: 'neutral' as const,
        }] : []),
      ]
    : [
        { id: 'current', label: metricCards[0]!.label, value: `${metricCards[0]!.value} stph`, sub: metricCards[0]!.sub, tone: 'neutral' },
        { id: 'maximum', label: metricCards[1]!.label, value: `${metricCards[1]!.value} stph`, sub: metricCards[1]!.sub, tone: 'ok' },
        { id: 'headroom', label: metricCards[2]!.label, value: metricCards[2]!.value, sub: metricCards[2]!.sub, tone: read.headroomStph >= 0 ? 'warn' : 'bad' },
        { id: 'bottleneck', label: metricCards[3]!.label, value: metricCards[3]!.value, sub: metricCards[3]!.sub, tone: 'bad' },
      ];
  return {
    selectedScenarioId,
    selectedHeadroomStph,
    selectedEbitdaA,
    metricCards,
    ladderStrip,
    ladderMeta: feedLimited
      ? `${stphOneDecimal(read.measuredThroughputStph)} stph ${read.sourceTags.currentFeed} feed`
      : (() => { const ladder = bottleneckLadderProjection(read.bottleneckLadder); return `${ladder.currentRate} · ${ladder.recoveryFloor}`; })(),
    feedLimited,
    constraints,
    chart: {
      categories: chartPoints.map((point) => String(point.throughputStph)),
      zn: chartPoints.map((point) => point.znPct),
      pb: chartPoints.map((point) => point.pbPct),
      currentIndex: Math.max(0, currentIndex),
      maximumIndex: Math.max(0, maximumIndex),
      selectedIndex: Math.max(0, selectedIndex),
      currentEqualsMaximum: currentIndex === maximumIndex,
      selectedLabel: currentIndex === maximumIndex ? 'Current / Maximum' : selectedScenario.label,
      floorPct: read.recoveryFloorZnPct,
      yDomain: [Math.max(0, chartLow - chartPadding), Math.min(100, chartHigh + chartPadding)] as const,
    },
    scenarios: scenarioCards(read),
    step1: upliftCard(read.step1, maximum.recoveries.znPct, maximum.recoveries.pbPct),
    step2: step2Card,
    waterfall,
    ai: [
      { label: 'Bottleneck', value: `${bottleneck.name} · ${bottleneck.utilisationPct.toFixed(0)}%`, tone: 'warn' },
      {
        label: 'Headroom',
        // Headroom is to the current bottleneck's ceiling (the metric card's basis); the next rung is its own row.
        value: selectedHeadroomStph > 0.05
          ? `${signedStphOneDecimal(selectedHeadroomStph)} to ${bottleneck.name} ${stphOneDecimal(read.maxAchievableStph)} stph ceiling from ${selectedScenario.label} ${stphOneDecimal(selectedScenario.throughputStph)} stph`
          : selectedHeadroomStph < -0.05
            ? `${stphOneDecimal(-selectedHeadroomStph)} stph over ${bottleneck.name} ${stphOneDecimal(read.maxAchievableStph)} stph ceiling at ${selectedScenario.label} ${stphOneDecimal(selectedScenario.throughputStph)} stph`
            : `${bottleneck.name} ${stphOneDecimal(read.maxAchievableStph)} stph ceiling reached at ${selectedScenario.label}`,
        tone: selectedHeadroomStph >= 0 ? 'ok' : 'bad',
      },
      {
        label: 'Scenario EBITDA/day',
        value: `${formatCurrency(selectedEbitdaA)}/day · ${selectedScenarioId === 'current' ? 'today baseline' : `${signedA(selectedEbitdaA - currentEbitdaA)} vs today ${formatCurrency(currentEbitdaA)}`}`,
        tone: signTone(selectedEbitdaA - currentEbitdaA),
      },
      { label: 'Step 1 EBITDA/day', value: signedA(read.step1.ebitdaGainA), tone: signTone(read.step1.ebitdaGainA) },
      {
        label: 'Next constraint',
        value: `${nextRung.name} · ${stphOneDecimal(nextRung.ceilingStph)} stph ceiling · ${signedStphOneDecimal(nextRung.ceilingStph - selectedScenario.throughputStph)} vs ${selectedScenario.label}`,
        tone: 'warn',
      },
    ],
  };
}
